import { Component } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Contribution } from '../../../../../../../common/model/input/contributions/types';
import { ModelEntityType } from '../../../../../../../common/model/input/types';
import { ModelEntitySearchResponseItem } from '../../../../../../../common/model/search/types';
import { getProductViewUrl } from '../../../../../../untidy/urls';
import { TextField } from '../../../../../generic/components/inputs/text-field';
import { themeDEPRECATED } from '../../../../../generic/global/styles';
import Search from '../../../output/search';
import {
  ContributionModalParagraph,
  ContributionModalTitle,
  Example,
  FormRow,
  FullRow,
  getContributeSearchStyle,
  Label,
  ProductCodeWithLabel,
  ProductRow,
  RemoveLink,
} from './common';
import { FormButton } from './form-button';

export const ProductLink = styled(Link)`
  cursor: pointer;
  &:hover {
    color: ${themeDEPRECATED.colors.pinkHover()};
  }
  text-decoration: none;
`;

interface State {
  comment?: string;
  /**
   * Used for both invalid and missing products.
   */
  products?: ProductCodeWithLabel[];
  newCompanyName?: string;
  newCompanyWebsite?: string;
  newCompanyWikipedia?: string;
  newCompanyNoWikipedia: boolean;
}

interface Props {
  isSubmitting: boolean;
  submitContribution: (options: { target?: string; data: Contribution }) => void;
}

export class NewCompanyForm extends Component<Props, State> {
  public state: State = {
    newCompanyNoWikipedia: false,
  };

  public render() {
    const { isSubmitting } = this.props;
    const { newCompanyName, newCompanyWebsite, newCompanyWikipedia, newCompanyNoWikipedia, comment, products } =
      this.state;

    const canSubmit = products && ((newCompanyWebsite && newCompanyName) || newCompanyWikipedia);

    return (
      <>
        <ContributionModalTitle>Which company would you like to see here?</ContributionModalTitle>
        <ContributionModalParagraph>
          We currently only show a small batch of example companies in the free public Playground. We will gradually add
          more companies. Which company's net impact profile would you like to see here?
        </ContributionModalParagraph>
        <ContributionModalParagraph>
          To identify the company in an unambiguous way, we ask you to give the URL to that company's Wikipedia profile.
          If it doesn't have one, just tick the little box below the field and we'll give you another option.
        </ContributionModalParagraph>
        <form
          onSubmit={e => {
            e.preventDefault();
          }}
        >
          <FormRow>
            <TextField
              label={
                <>
                  <Label>URL to the company's Wikipedia page</Label>
                  <Example>Example: http://en.wikipedia.org/wiki/Apple_Inc</Example>
                </>
              }
              id="new-company-wikipedia"
              type="text"
              value={newCompanyWikipedia}
              disabled={isSubmitting || newCompanyNoWikipedia}
              onChange={value => {
                this.setState({ newCompanyWikipedia: value });
              }}
            />
          </FormRow>
          <div style={{ marginTop: themeDEPRECATED.rhythm(3) }}>
            <input
              type="checkbox"
              checked={newCompanyNoWikipedia}
              id="no-wikipedia-page"
              onChange={e => {
                this.setState({ newCompanyNoWikipedia: e.target.checked });
              }}
            />{' '}
            <label htmlFor="no-wikipedia-page" style={{ lineHeight: 1.45 }}>
              This company doesn't have a Wikipedia page
            </label>
          </div>
          {newCompanyNoWikipedia && (
            <>
              <FormRow>
                <TextField
                  label="Name of the company (e.g. Apple Inc.)"
                  id="new-company-name"
                  type="text"
                  value={newCompanyName}
                  disabled={isSubmitting}
                  onChange={value => {
                    this.setState({ newCompanyName: value });
                  }}
                />
              </FormRow>
              <FormRow>
                <TextField
                  label="URL to the company's website"
                  id="new-company-website"
                  type="text"
                  value={newCompanyWebsite}
                  disabled={isSubmitting}
                  onChange={value => {
                    this.setState({ newCompanyWebsite: value });
                  }}
                />
              </FormRow>
            </>
          )}
          <FormRow>
            <FullRow>
              <Label>What products or services does this company produce? You can list as many as you know.</Label>
              <Example>Example: for Apple, you could add laptops, tablet computers, smart phones, …</Example>
              {products &&
                products.map(productDatum => (
                  <ProductRow key={productDatum.productCode}>
                    <ProductLink target="_blank" to={getProductViewUrl(productDatum.productCode, productDatum.label)}>
                      {productDatum.label}{' '}
                    </ProductLink>
                    <RemoveLink
                      onClick={() => {
                        this.setState(state => ({
                          products:
                            state.products && state.products.filter(p => p.productCode !== productDatum.productCode),
                        }));
                      }}
                    >
                      (remove)
                    </RemoveLink>
                  </ProductRow>
                ))}
              <Search
                creatable="Suggest a new product:"
                onChange={(option?: ModelEntitySearchResponseItem) => {
                  if (!option) {
                    return;
                  }
                  this.setState(state => {
                    return {
                      products: (state.products || []).concat({
                        productCode: option.id,
                        userCreated: option.created,
                        label: option.name,
                      }),
                    };
                  });
                }}
                searchTypes={[ModelEntityType.PRODUCT, ModelEntityType.MIXIN]}
                styleOverride={getContributeSearchStyle()}
              />
            </FullRow>
          </FormRow>
          <FormRow>
            <TextField
              label="Why is this company's net impact interesting to you?"
              id="new-company-reason"
              type="textarea"
              value={comment}
              disabled={isSubmitting}
              metaLabel="Optional"
              onChange={value => {
                this.setState({ comment: value });
              }}
            />
          </FormRow>
          <FormRow>
            <FormButton
              type="submit"
              disabled={isSubmitting || !canSubmit}
              onClick={() => {
                let newCompany: { website: string; name: string } | { wikipediaURL: string } | undefined;
                if (this.state.newCompanyWebsite && this.state.newCompanyName) {
                  newCompany = {
                    website: this.state.newCompanyWebsite,
                    name: this.state.newCompanyName,
                  };
                } else if (this.state.newCompanyWikipedia) {
                  newCompany = { wikipediaURL: this.state.newCompanyWikipedia };
                }
                if (newCompany && this.state.products) {
                  this.props.submitContribution({
                    data: {
                      type: 'NEW_COMPANY',
                      newCompany,
                      comment: this.state.comment,
                      products: this.state.products.filter(p => !p.userCreated).map(p => p.productCode),
                      newProducts: this.state.products.filter(p => p.userCreated).map(p => p.label),
                    },
                  });
                }
              }}
              style={{ width: '100%' }}
            >
              Submit
            </FormButton>
          </FormRow>
        </form>
      </>
    );
  }
}
